import Head from 'next/head';
import { BlogPost } from '../../lib/blog';
import {
  AUTHOR_NAME,
  BLOG_ID,
  SITE_OG_IMAGE,
  SITE_URL,
  articleAuthor,
  blog,
  breadcrumb,
  graph,
  howTo,
  ld,
  orgRef,
  person,
  questions,
  websiteRef,
} from '../../lib/schema';

interface BlogSEOProps {
  post?: BlogPost;
  /** Index only: the articles the page lists, so `blogPost` mirrors the grid. */
  posts?: BlogPost[];
  isBlogHome?: boolean;
}

export const BlogSEO: React.FC<BlogSEOProps> = ({ post, posts = [], isBlogHome = false }) => {
  const siteUrl = SITE_URL;
  // Articles are canonical on the main domain. blog.soulminter.io 301s here
  // (see src/middleware.ts) so a single URL per article accrues authority.
  const blogUrl = `${siteUrl}/blog`;
  const faviconUrl = '/favicon.ico';

  if (isBlogHome) {
    const blogDescription =
      'Learn how to create Solana tokens, understand SPL tokens, and master token creation with our comprehensive guides and tutorials.';
    const blogTrail = [
      { name: 'SoulMinter', url: siteUrl },
      { name: 'Blog', url: blogUrl },
    ];

    /**
     * `blogPost` lists every article the index renders. One fetch of /blog
     * therefore tells an answer engine the full set of articles, their URLs
     * and their dates, instead of forcing it to crawl and guess.
     */
    const blogNode = {
      ...blog,
      description: blogDescription,
      blogPost: posts.map((item) => ({
        '@type': 'BlogPosting',
        '@id': `${blogUrl}/${item.slug}#article`,
        headline: item.title,
        description: item.description,
        url: `${blogUrl}/${item.slug}`,
        datePublished: item.date,
        dateModified: item.updated || item.date,
        author: articleAuthor(item.author),
        keywords: item.tags.join(', '),
      })),
    };

    const indexGraph = graph([
      blogNode,
      {
        '@type': 'CollectionPage',
        '@id': `${blogUrl}#webpage`,
        url: blogUrl,
        name: 'Blog | SoulMinter - Solana Token Creation Guides',
        description: blogDescription,
        isPartOf: websiteRef,
        about: orgRef,
        publisher: orgRef,
        inLanguage: 'en',
        breadcrumb: { '@id': `${blogUrl}#breadcrumb` },
        mainEntity: { '@id': BLOG_ID },
      },
      breadcrumb(blogUrl, blogTrail),
    ]);

    return (
      <Head>
        <title>Blog | SoulMinter - Solana Token Creation Guides</title>
        <meta name="description" content={blogDescription} />
        <meta name="keywords" content="Solana blog, token creation guide, SPL token tutorial, crypto education, blockchain learning" />
        <meta name="robots" content="index, follow" />

        {/* Favicons */}
        <link rel="shortcut icon" href={faviconUrl} />
        <link rel="icon" href={faviconUrl} />
        <link rel="apple-touch-icon" href={faviconUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:title" content="Blog | SoulMinter - Solana Token Creation Guides" />
        <meta property="og:description" content={blogDescription} />
        <meta property="og:image" content={SITE_OG_IMAGE} />
        <meta property="og:site_name" content="SoulMinter Blog" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={blogUrl} />
        <meta name="twitter:title" content="Blog | SoulMinter - Solana Token Creation Guides" />
        <meta name="twitter:description" content={blogDescription} />
        <meta name="twitter:image" content={SITE_OG_IMAGE} />

        <link rel="canonical" href={blogUrl} />

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: ld(indexGraph) }}
        />
      </Head>
    );
  }

  if (!post) {return null;}

  const postUrl = `${blogUrl}/${post.slug}`;
  const rawImage = post.image || SITE_OG_IMAGE;
  // Social crawlers require an absolute og:image URL. Local thumbnails in
  // /public are stored as root-relative paths (e.g. /blog/foo.png), so resolve
  // those against the site origin -- not blogUrl, which now carries a /blog
  // path segment and would double it. Absolute (http...) images pass through.
  const postImage = rawImage.startsWith('http') ? rawImage : `${siteUrl}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;

  const postTrail = [
    { name: 'SoulMinter', url: siteUrl },
    { name: 'Blog', url: blogUrl },
    { name: post.title, url: postUrl },
  ];

  const faqs = post.faq ?? [];

  /**
   * The article page node. Typed FAQPage as well when the article carries a
   * frontmatter FAQ, because BlogPostComponent renders those questions at the
   * end of the body -- the markup and the page agree by construction.
   */
  const pageNode: Record<string, unknown> = {
    '@type': faqs.length ? ['WebPage', 'FAQPage'] : 'WebPage',
    '@id': `${postUrl}#webpage`,
    url: postUrl,
    name: post.title,
    description: post.description,
    isPartOf: websiteRef,
    publisher: orgRef,
    inLanguage: 'en',
    breadcrumb: { '@id': `${postUrl}#breadcrumb` },
    primaryImageOfPage: { '@type': 'ImageObject', url: postImage },
    datePublished: post.date,
    dateModified: post.updated || post.date,
  };

  if (faqs.length) {
    pageNode.mainEntity = questions(faqs);
  }

  const articleNode: Record<string, unknown> = {
    '@type': 'BlogPosting',
    '@id': `${postUrl}#article`,
    headline: post.title,
    description: post.description,
    url: postUrl,
    datePublished: post.date,
    dateModified: post.updated || post.date,
    inLanguage: 'en',
    isPartOf: { '@id': BLOG_ID },
    mainEntityOfPage: { '@id': `${postUrl}#webpage` },
    author: articleAuthor(post.author),
    publisher: orgRef,
    image: {
      '@type': 'ImageObject',
      url: postImage,
    },
    keywords: post.tags.join(', '),
    articleSection: post.tags[0] || 'Solana',
    wordCount: post.content.split(/\s+/).filter(Boolean).length,
    // schema.org types `timeRequired` as a Duration, so it takes ISO 8601 --
    // the "6 min read" string the page renders is not a valid value for it.
    timeRequired: `PT${post.readingMinutes}M`,
  };

  /**
   * Outbound citations, from the same array the article renders as its visible
   * Sources list. Declaring what an article is sourced from is the one signal
   * generative engines weigh most heavily when deciding whether to quote it.
   */
  if (post.sources?.length) {
    articleNode.citation = post.sources.map((source) => ({
      '@type': 'WebPage',
      name: source.name,
      url: source.url,
    }));
  }

  const articleGraph = graph([
    blog,
    // Defined in full here, not just referenced: an article by the site author
    // is where the author card renders that same name and bio.
    post.author === AUTHOR_NAME ? person : null,
    pageNode,
    articleNode,
    breadcrumb(postUrl, postTrail),
    post.howTo
      ? howTo({
          id: `${postUrl}#howto`,
          pageUrl: postUrl,
          name: post.howTo.name,
          description: post.howTo.description,
          totalTime: post.howTo.totalTime,
          steps: post.howTo.steps,
        })
      : null,
  ]);

  return (
    <Head>
      <title>{`${post.title} | SoulMinter Blog`}</title>
      <meta name="description" content={post.description} />
      <meta name="keywords" content={post.tags.join(', ')} />
      <meta name="author" content={post.author} />
      <meta name="robots" content="index, follow" />

      {/* Favicons */}
      <link rel="shortcut icon" href={faviconUrl} />
      <link rel="icon" href={faviconUrl} />
      <link rel="apple-touch-icon" href={faviconUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:url" content={postUrl} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.description} />
      <meta property="og:image" content={postImage} />
      <meta property="og:site_name" content="SoulMinter Blog" />
      <meta property="article:published_time" content={post.date} />
      {post.updated && (
        <meta property="article:modified_time" content={post.updated} />
      )}
      <meta property="article:author" content={post.author} />
      {post.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={postUrl} />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.description} />
      <meta name="twitter:image" content={postImage} />

      <link rel="canonical" href={postUrl} />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: ld(articleGraph) }}
      />
    </Head>
  );
};
